import * as yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum DiversifiedBotanicalsSalesOrderLineItemKeys {
  lineItemNumber = 'lineItemNumber', // line item number, not to be entered by user
  itemNumber = 'itemNumber', // user entered item number
  description = 'description', // verbose description of the item (human parseable)
  unitPrice = 'unitPrice', // price per unit
  itemQty = 'itemQty', // quantity of the item for order
  unitOfMeasure = 'unitOfMeasure', // unit of measure for the item
  itemDiscountRate = 'itemDiscountRate', // discount rate for the item
  itemDiscountPrice = 'itemDiscountPrice', // discount price for the item
  lineTotal = 'lineTotal', // total cost per (unitPrice * itemQty), not to be entered by user
  lotNumber = 'lotNumber', // lot/batch number per line item
}

export const diversifiedBotanicalsSalesOrderOrganizationAddressSchema = yup.object().shape({
  addressLine1: yup.string().nullable().optional(),
  addressLine2: yup.string().nullable().optional(),
  postalCode: yup.string().nullable().optional(),
  city: yup.string().nullable().optional(),
  state: yup.string().nullable().optional(),
  country: yup.string().nullable().optional(),
})
export type DiversifiedBotanicalsSalesOrderOrganizationAddress = yup.InferType<
  typeof diversifiedBotanicalsSalesOrderOrganizationAddressSchema
>

export const diversifiedBotanicalsSalesOrderFileDataSchema = yup.object().shape({
  issuer: yup.object().shape({
    companyName: yup.string().nullable().optional(),
    address: diversifiedBotanicalsSalesOrderOrganizationAddressSchema.nullable().optional(),
    attn: yup.string().nullable().optional(),
    phoneNumber: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
    website: yup.string().nullable().optional(),
  }),
  orderNumber: yup.string().nullable().optional(),
  customerNumber: yup.string().nullable().optional(),
  issueDate: yup.string().nullable().optional(),
  dueDate: yup.string().nullable().optional(),
  confirmTo: yup.string().nullable().optional(),
  shipDate: yup.string().nullable().optional(),
  paymentTerms: yup.string().nullable().optional(),
  fobPoint: yup.string().nullable().optional(),
  contactName: yup.string().nullable().optional(),
  contactPhone: yup.string().nullable().optional(),
  customerPoNumber: yup.string().nullable().optional(),
  shippingMethod: yup.string().nullable().optional(),
  shippingCost: yup.string().nullable().optional(),
  shipmentNumber: yup.string().nullable().optional(),
  shippingTerms: yup.string().nullable().optional(),
  trackingNumber: yup.string().nullable().optional(),
  currency: yup.string().nullable().optional(),
  locale: yup.string().nullable().optional(),
  customer: yup.object().shape({
    billingDetails: yup
      .object()
      .shape({
        companyName: yup.string().nullable().optional(),
        billingAddress: diversifiedBotanicalsSalesOrderOrganizationAddressSchema
          .nullable()
          .optional(),
        attn: yup.string().nullable().optional(),
        phoneNumber: yup.string().nullable().optional(),
        emailAddress: yup.string().nullable().optional(),
      })
      .nullable()
      .optional(),
    shippingDetails: yup
      .object()
      .shape({
        companyName: yup.string().nullable().optional(),
        shippingAddress: diversifiedBotanicalsSalesOrderOrganizationAddressSchema
          .nullable()
          .optional(),
        attn: yup.string().nullable().optional(),
        phoneNumber: yup.string().nullable().optional(),
        emailAddress: yup.string().nullable().optional(),
      })
      .nullable()
      .optional(),
  }),
  items: yup
    .array()
    .of(
      yup.object().shape({
        lineItemNumber: yup.number().nullable().optional(),
        itemNumber: yup.string().nullable().optional(),
        description: yup.string().nullable().optional(),
        unitPrice: yup.string().nullable().optional(),
        itemQty: yup.string().nullable().optional(),
        unitOfMeasure: yup.string().nullable().optional(),
        itemDiscountPrice: yup.string().nullable().optional(),
        lineTotal: yup.string().nullable().optional(),
        lotNumber: yup.string().nullable().optional(),
      }),
    )
    .required(),
  taxRate: yup.string().nullable().optional(),
  paymentMethod: yup.string().nullable().optional(),
  paymentAmount: yup.string().nullable().optional(),
  totalPayments: yup.string().nullable().optional(),
  discountAmount: yup.string().nullable().optional(),
  paymentsDeposit: yup.string().nullable().optional(),
  documentBalance: yup.string().nullable().optional(),
})

export type DiversifiedBotanicalsSalesOrderFileData = yup.InferType<
  typeof diversifiedBotanicalsSalesOrderFileDataSchema
>
type RequiredFileData = MakeRequiredAndNullable<DiversifiedBotanicalsSalesOrderFileData>

export const diversifiedBotanicalsSalesOrderFileMappings: RequiredFileData = {
  issuer: {
    companyName: '9e3a4e1a-57bd-4f48-877f-e0f2c75ad7c3',
    address: {
      addressLine1: '96f7827d-6d2c-46c4-a58e-3291f8c24dd7',
      addressLine2: 'd4e829f1-f49d-4905-9be2-881d7a0ca209',
      postalCode: 'ad5a640a-e471-4e4e-ac31-b2f8fe8fb4b6',
      city: '882756d0-ac13-401d-b4dc-6eb5f54f4536',
      state: '84222ead-4fcd-4af5-b160-0acf470d1ab3',
      country: '56ff1242-3fe1-4d1d-b087-9a5a86bdb122',
    },
    attn: 'b3d64924-9a30-4075-b077-b0921a1e23c7',
    phoneNumber: '9602e3be-7fea-4986-8414-64a96f5c896b',
    emailAddress: 'ab51108c-6357-446b-a584-70477a79abf3',
    website: '2afd898e-8375-4e07-bdda-35366f496620',
  },
  orderNumber: 'a6c6e2a1-78e2-47b5-8fc7-8684aa4482c9',
  customerNumber: '2ac8ac22-bc6b-4ceb-a9c3-5365fcc5ad51',
  issueDate: 'aec9ef1b-2f1b-482f-8eb2-ac5b41758331',
  dueDate: 'fe567561-7731-4f73-854a-5c1842072e5c',
  confirmTo: '8a3efe6d-f294-4c65-bf6d-29c883dc0449',
  shipDate: 'f7a88990-dbd0-4a4c-8af2-eefe1def8a3f',
  paymentTerms: 'fd24c1f7-8ecb-49ec-962e-f86a7206df27',
  fobPoint: 'e4bd9002-4e72-4793-acbb-479031db34e2',
  contactName: 'bb918269-de8c-47d8-ad38-406c38a2b406',
  contactPhone: 'ba588c17-dd21-4144-b01f-55ca306df337',
  customerPoNumber: '1b7e8437-c790-49b8-aa52-ed6ec3855174',
  shippingMethod: '6926ab7f-5bc2-498e-9b23-65b615016cd0',
  shippingCost: 'd1842f84-2c10-460d-aebd-f93cb79eb41e',
  shipmentNumber: '645a034c-f643-4630-a15c-f44bbeee98e1',
  shippingTerms: '06958619-2768-42b2-b99d-d7bc3e571811',
  trackingNumber: '8dd65d1c-f915-4f29-82ff-3c7179533d3f',
  currency: '2ff6c354-a5ed-4f6e-b868-0c4d0033219a',
  locale: 'ac68d1ee-7bd2-41cf-832a-424d63a5a82a',
  customer: {
    billingDetails: {
      companyName: 'c1943eca-0294-492d-8294-0c0164e2d329',
      billingAddress: {
        addressLine1: '1669b4aa-fa4b-4af2-8301-2bfa904586fe',
        addressLine2: 'ff2e845c-9c8f-4b6a-b202-92f83977a73d',
        postalCode: '69f46275-74a7-475c-9614-a23ae96246c9',
        city: 'e02f8d36-c1ce-4fb9-97fb-0a4de06beaea',
        state: 'ae1b35d6-5f2a-48ce-8b3d-4e71090825f6',
        country: '857d6767-6f7c-4248-8139-c5f7880c780a',
      },
      attn: '61b31e45-a112-4926-b776-ae5def7289a4',
      phoneNumber: '11ea5267-e4aa-4298-a91d-9c32adfc3239',
      emailAddress: 'db46e1c0-7b67-43a2-a395-d09c4c7fc1f2',
    },
    shippingDetails: {
      companyName: 'bb2ca951-33e1-4e4d-aba3-84890fd03fcf',
      shippingAddress: {
        addressLine1: '0778366f-23f5-464f-9ade-e0033142cfc9',
        addressLine2: '80df218e-b914-4814-9a50-79c74c464a43',
        postalCode: 'ed28ad68-8b29-4f9a-99f9-9558846260a2',
        city: 'a5ee4614-850e-45fe-aab7-beae64b8510f',
        state: 'ec8707cf-093c-44d3-853c-dcef89b6603c',
        country: '26c42e5c-6a2a-420a-898c-224a2dd0010c',
      },
      attn: '6fecc931-bf14-4fe7-b69d-f891a9cd0aa1',
      phoneNumber: 'eb48f90d-cf8a-409e-b6b8-0a70aab65154',
      emailAddress: '8a0c4a04-a216-4bcb-9956-8e39e6b9263e',
    },
  },
  items: [
    {
      lineItemNumber: null,
      itemNumber: '976a7a63-94d5-4ab9-aae1-c6c096bbe904',
      description: '4eb025db-b9f1-4c58-bfe7-8ca9ad86e888',
      unitPrice: '610b8055-9929-4615-84aa-7fe591831030',
      itemQty: '8a128116-220f-4a47-83e9-eded5e76a1f0',
      unitOfMeasure: '0aa93d5a-0b5a-4d4e-a0db-37caa76720fb',
      itemDiscountPrice: '4290e792-09f5-4492-bd3c-8b78cbc2defb',
      lineTotal: null,
      lotNumber: 'a4195877-d4d9-4ae2-a8af-e1274a970a30',
    },
  ],
  taxRate: '210c3484-741a-489e-a72b-e9db78d9e875',
  paymentMethod: 'c745afe8-5d38-4dac-b606-a70cbaff22c0',
  paymentAmount: 'cec37d09-d18f-4cc4-b54d-a192e6c113be',
  totalPayments: '595b069e-9079-4d7d-8057-695ed07bd5cf',
  discountAmount: '25934072-3343-4140-9775-2bf23fcbecf3',
  paymentsDeposit: '385384ea-eec3-479f-820f-45f90837fa08',
  documentBalance: 'e0fbce7e-c153-4600-a7f3-bcd27e46d43e',
}
